/**
 * TIER 2: Shared FHIR Hooks - useFHIRResource
 *
 * FHIR-specific resource management hook that provides:
 * - FHIR-compliant error handling
 * - Automatic reference resolution
 * - FHIR search parameter support
 * - Bundle and transaction handling
 *
 * This hook builds on the common useApiResource hook and adds FHIR-specific functionality.
 */

// Hooks
import { useCallback } from "react";
import {
  useApiResource,
  type UseApiResourceOptions,
  type UseApiResourceResult,
} from "../common/useApiResource";

// Types
import type {
  Resource,
  ResourceType,
  OperationOutcome,
} from "@medplum/fhirtypes";

/** Options for useFHIRResource (includeReferences, onFHIRError). @agentic @benchmark/core */
export type FHIRResourceOptions<T extends Resource> = {
  includeReferences?: string[];
  onFHIRError?: (outcome: OperationOutcome) => void;
  onError?: (error: Error) => void;
} & Omit<UseApiResourceOptions<T>, "onError" | "resourceType">;

/** Result from useFHIRResource. @agentic @benchmark/core */
export type UseFHIRResourceResult<T extends Resource> = {
  operationOutcome: OperationOutcome | null;
  isFHIRError: boolean;
} & UseApiResourceResult<T>;

/**
 * FHIR resource hook with reference resolution and OperationOutcome handling.
 * @agentic @benchmark/core
 */
function useFHIRResource<T extends Resource>(
  resourceType: ResourceType,
  id?: string,
  options: FHIRResourceOptions<T> = {},
): UseFHIRResourceResult<T> {
  const {
    includeReferences = [],
    onFHIRError,
    onError,
    searchParams: baseSearchParams,
    ...restOptions
  } = options;

  // Enhance search parameters with FHIR-specific options
  const searchParams = {
    ...baseSearchParams,
    ...(includeReferences.length > 0 && {
      _include: includeReferences.join(","),
    }),
  };

  // Enhanced error handling for FHIR-specific errors
  const handleError = useCallback(
    (error: Error) => {
      // Check if this is a FHIR OperationOutcome error
      if ("outcome" in error && error.outcome) {
        onFHIRError?.(error.outcome as OperationOutcome);
      }

      // Call the general error handler
      onError?.(error);
    },
    [onFHIRError, onError],
  );

  const result = useApiResource<T>({
    resourceType,
    id,
    searchParams:
      Object.keys(searchParams).length > 0 ? searchParams : undefined,
    onError: handleError,
    ...restOptions,
  });

  // Extract OperationOutcome from error if available
  const operationOutcome =
    result.error && "outcome" in result.error
      ? (result.error as Error & { outcome: OperationOutcome }).outcome
      : null;

  const isFHIRError = !!operationOutcome;

  return {
    ...result,
    operationOutcome,
    isFHIRError,
  };
}

/** Convenience hook for Organization resource. @agentic @benchmark/core */
export function useOrganizationResource(
  id?: string,
  options: Omit<FHIRResourceOptions<Resource>, "resourceType"> = {},
) {
  return useFHIRResource("Organization", id, options);
}

/** Convenience hook for Patient resource. @agentic @benchmark/core */
export function usePatientResource(
  id?: string,
  options: Omit<FHIRResourceOptions<Resource>, "resourceType"> = {},
) {
  return useFHIRResource("Patient", id, options);
}
